import React, { useContext, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import cn from 'classnames'
import FavoritePoseItem from '../../../../../favorite-pose-item'
import styles from './FavoritePoseSelection.module.sass'
import Typography from '../../../../../Typography'
import Button from '../../../../../Button'
import classes from '../../../../favorite-pose-selection/favorite-pose-selection.module.sass'
import CloseIcon from '../../../../../svg-icons/close-icon'
import PreviewImage from '../../../../../PreviewImage'
import Modal from '../../../../../modal'
import { routes } from '../../../../../../routes'
import heartIcon from '../../../../../../assets/favorite_all.png'
import fullHeartIcon from '../../../../../../assets/heart.png'
import Image from "../../../../../Image";
import useImageOrientations from '../../../../../../hooks/useImageOrientations';
import usePackagePoses from '../../../../../../hooks/usePackagePoses';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import background from '../../../../../../models/Background';
import usePricelist from '../../../../../../hooks/usePricelist';
import { CustomerContext } from '../../../../../../contexts/CustomerContext'
import useBuyAllOption from "../../../../../../hooks/useBuyAllOption";
import axios from 'axios';
import useLocalStorage from "../../../../../../hooks/useLocalStorage";
import useAddons from "../../../../../../hooks/useAddons";
import useOfferCoupon from '../../../../../../hooks/useOfferCoupon';
import BuyAllCard from '../../../../../BuyAllCard';
import { afterBuildFreeMagnetsRoute } from '../../../FreeMagnets/freeMagnetsVisibility';
import { freeMagnetsStatus } from '../../../../../../utils/freeMagnets';
import QueryKeys from '../../../../../../utils/QueryKeys';
import { recordFavoriteSelection } from '../../../../../../utils/recordFavoriteSelection';
import { toggleFavorite } from '../../../../../../utils/favoriteToggle';

// Case-insensitive comparator for season values. Production data has mixed
// casing (e.g. package_poses.season = "Spring2026" while
// student_images.folder = "spring2026"), so a strict === silently mismatches
// the spring side and falls back to backgrounds[0].
const seasonMatches = (a, b) => (a || '').toLowerCase() === (b || '').toLowerCase();

// A package_pose row whose Season dropdown was left blank. The admin form shows Season as
// optional beside a required Background, so a blank one means "this backdrop, whatever
// season the photo is from" — NOT "this row matches nothing".
const isSeasonless = (packagePose) => !(packagePose.season || '').trim();

// The pose NUMBER the pricelist keys its spec on, from the filename's trailing `-NN.ext`
// (`19-U002M-019RXMMZZF-01.png` -> "01"). Zero-padded, exactly as package_poses.pose_number
// stores it, so the two compare directly.
export const poseNumberToken = (pose) => String(pose?.url || '').split('-').reverse()[0].split('.')[0];

// Which of a student's poses the gallery shows: the pricelist's pose numbers --
// and, on a SPORT pricelist, every photo the photographer uploaded as well
// (`sportUploadSlot`, the server's Pose#sport_upload_slot?). That is the rule
// Pose.for_package applies; the pricelist only decides the derived products.
// Without it pricelist 976, which lists 04, hid a player's second team picture
// and every buddy above 15.
export const galleryPoses = (images, packagePoses, isSport) => {
  if (!packagePoses || packagePoses.length === 0) return images;

  return images.filter((image) => (
    (isSport && image.sportUploadSlot)
      || packagePoses.some((pp) => pp.poseNumber === poseNumberToken(image))
  ));
};

// THE lookup: which package_pose configures this pose. One implementation — the backdrop
// and the graphics overlay must never disagree about which row applies.
//
// Season only narrows the search on a MULTI-SEASON pricelist, where the same pose number
// legitimately appears once per season with a different backdrop (package 639: pose "03"
// for fall2025 and again unseasoned). There, SPECIFIC BEATS GENERIC — an exact season match
// wins, and a seasonless row is the fallback — so adding a blank row can never shadow a
// season-specific one that sits above it.
//
// The fallback is the bug this was written for. `multiple_seasons` is set on pricelists that
// only ever serve ONE season (778 "JH-HS Fall 2027" carries ["fall2027"]), and its 11 poses
// were all saved with a blank Season. Requiring an exact season match therefore matched
// NOTHING, and every pose in the emailed JHHS gallery fell back to backgrounds[0] — the
// package's first available backdrop, not the one the pricelist assigns it.
export const findPackagePose = (packagePoses, pose, pricelist) => {
  if (!packagePoses || packagePoses.length === 0) return undefined;

  const poseNumber = poseNumberToken(pose);
  const forNumber = packagePoses.filter((packagePose) => packagePose.poseNumber === poseNumber);

  if (!pricelist?.multipleSeasons?.length) return forNumber[0];

  return forNumber.find((packagePose) => seasonMatches(packagePose.season, pose.folder))
    || forNumber.find(isSeasonless);
};

export const getAppropriateBg = (packagePoses, pose, backgrounds, pricelist) => {
  // An unconfigured pricelist and an unconfigured POSE land in the same place: the package's
  // first available backdrop, rather than a bare cutout. findPackagePose already returns
  // undefined for an empty spec, so this one fallback covers both.
  const preferred = findPackagePose(packagePoses, pose, pricelist);

  if (!preferred) return backgrounds[0];

  return preferred.background;
};

export const getAppropriateForeground = (packagePoses, pose, pricelist) => {
  const preferred = findPackagePose(packagePoses, pose, pricelist);

  if (!preferred) return undefined;

  return preferred.foreground;
};

const renderInstructions = () => (
  <div className={styles.instructions}>
    <Typography align="center" weight="bold">
      Choose your Favorites
    </Typography>
  </div>
)

// The redesigned header: "Melina's Third Grade Portraits" over a "Choose Your Favorites"
// banner, matching the senior gallery. Opt-in via the `headline` prop — this component is
// shared with several other flows (sittings, individual prints, past images), and they
// keep the plain instruction line until they're redesigned too.
const renderHeader = (headline) => (
  <>
    <div className="bg-white py-8 text-center rounded-b-lg shadow-sm">
      <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900">{headline}</h1>
    </div>
    <div className="mt-2 bg-shoob-blue text-white rounded-md px-5 py-4 text-center">
      <p className="text-lg leading-tight font-semibold">Choose Your Favorites</p>
    </div>
  </>
);

const renderModal = (modalImage, bg, foreground, onClick) => (
  <Modal open>
    <div className={classes.modalContent}>
      <div className={classes.iconWrapper}>
        <CloseIcon className={classes.closeIcon} onClick={onClick} />
      </div>
      <PreviewImage foregroundUrl={modalImage} backgroundUrl={bg?.imageUrl || backgrounds[0]?.imageUrl} actualForegroundUrl={foreground?.imageUrl} />
    </div>
  </Modal>
)

/**
 * Component
 */
const FavoritePoseSelection = ({
  background, backgrounds, images = [], seniors = false, headline = null,
}) => {
  const history = useHistory()
  const { packageId } = useParams()
  const [modalImage, setModalImage] = useState()
  const [favorites, setFavorites] = useState([])
  // Whether "Favorite All" was pressed, for recordFavoriteSelection.
  const [usedFavoriteAll, setUsedFavoriteAll] = useState(false)
  const { data: pricelist } = usePricelist(packageId);
  const { data: packagePoses } = usePackagePoses(packageId);
  const [image, setImage] = useState();
  const { customer, setCustomer } = useContext(CustomerContext)
  const { data: buyAllOption } = useBuyAllOption(packageId, customer?.currentSchool?.id);
  const [currentSeason] = useLocalStorage(`currentSeason-${customer?.currentStudent?.id}`)
  const { data: addons } = useAddons(!seniors);
  // The live offer for THIS pricelist -- the Buy All card reads its price/ribbon
  // from it (issue #5218 item 6) and the magnet-page detour asks it.
  const { data: offer } = useOfferCoupon(customer?.currentStudent?.studentId, customer?.currentSchool?.id, packageId);
  const queryClient = useQueryClient();

  // prime the url cache to toggle between landscape and portrait backgrounds
  useImageOrientations((backgrounds || []).map((bg) => bg.imageUrl));

  // The poses configured in the package, plus every upload on a sport one.
  const filteredImages = galleryPoses(images, packagePoses, pricelist?.isSport);

  // Check if all poses are JPG
  const allPosesAreJpg = filteredImages.length > 0 && filteredImages.every(image =>
    image.imageUrl && image.imageUrl.toLowerCase().includes('.jpg')
  );

  const onFavoriteClick = (id) => {
    const next = toggleFavorite({ favorites, usedFavoriteAll }, id)
    setFavorites(next.favorites)
    setUsedFavoriteAll(next.usedFavoriteAll)
  }

  const favoriteAll = () => {
    setUsedFavoriteAll(true)
    setFavorites(filteredImages.map((image) => image.id))
  }

  const handleBuyAll = async () => {
    const response = await axios.post(`/carts/${customer?.cart?.id}/buy_all`,
      { package_id: packageId, option_id: buyAllOption.id, student_id: customer.currentStudent.id, season: currentSeason }
    );

    const orderPackageId = response.data?.order_package_id;

    // Buy All Digital qualifies for the free magnets (issue #5218): detour
    // through the magnet page on the way to confirm, deciding from the FRESH
    // flags the /buy_all response carries (never the cached cart).
    queryClient.invalidateQueries({ queryKey: [QueryKeys.CART] });
    const status = await freeMagnetsStatus({ data: response.data, cartId: customer?.cart?.id });
    history.push(afterBuildFreeMagnetsRoute({
      next: routes.confirm(orderPackageId), orderPackageId, offer, status, allJpg: allPosesAreJpg,
    }))
  }

  const onContinue = () => {
    if (favorites.length === 0) {
      alert('Please select a pose to continue to the next page')
      return
    }
    recordFavoriteSelection({
      cartId: customer?.cart?.id,
      studentId: customer?.currentStudent?.id,
      packageId,
      poseIds: favorites,
      posesOffered: filteredImages.length,
      usedFavoriteAll,
    })
    if (seniors) {
      // Senior flow inserts a REQUIRED yearbook-pose step before packages so every
      // senior submits a yearbook pose of record in one pass. Elementary is unchanged.
      history.push(routes.seniors.packageBuilder(packageId, {favorites, step: 'yearbook-pose'}));
    } else {
      history.push(routes.elementaryMultiPoseBuilder(packageId, {favorites, step: 'select-package'}));
    }
  }

  const bgForModal = (foreground, pricelist) => {
    const priorityBg = getAppropriateBg(packagePoses, foreground, backgrounds, pricelist);

    return priorityBg || backgrounds[0];
  };

  const foregroundForModal = (foreground, pricelist) => {
    const f = getAppropriateForeground(packagePoses, foreground, pricelist);

    return f;
  };

  const handleImageClick = (foreground, foregroundUrl) => {
    setModalImage(foregroundUrl);
    setImage(foreground);
  }

  const isFetching = useIsFetching();

  if (isFetching) return null;

  return (
    <div style={{ maxWidth: '992px', margin: '0 auto' }}>
      <div className={styles.imageContainer}>
        {headline ? renderHeader(headline) : renderInstructions()}

        {modalImage && renderModal(modalImage, bgForModal(image, pricelist), foregroundForModal(image, pricelist), () => setModalImage(undefined))}

        <div className={cn(styles.container, modalImage && styles.hidden, filteredImages.length === 1 ? styles.onePoseContainer : '')}>
          {buyAllOption && !allPosesAreJpg && (
            // The real price + live-offer ribbon over the (soon price-free) art,
            // not the banner PNG as the whole card (issue #5218 item 6).
            <div className={styles.buyAll} style={{ position: 'relative', aspectRatio: '4 / 5' }}>
              <BuyAllCard
                option={buyAllOption}
                offer={offer}
                onClick={handleBuyAll}
                label="Download All My Photos"
                customer={customer}
                packageId={packageId}
              />
            </div>
          )}

          {filteredImages.map((image, i) => {
            let bg = backgrounds ? backgrounds[i % backgrounds.length].imageUrl : background;
            const priorityBg = getAppropriateBg(packagePoses, image, backgrounds, pricelist);
            const foreground = getAppropriateForeground(packagePoses, image, pricelist);

            return (
              <FavoritePoseItem
                index={i}
                key={image.id}
                onImageClick={handleImageClick}
                background={bg}
                priorityBg={priorityBg}
                backgrounds={backgrounds || []}
                onFavoriteClick={onFavoriteClick}
                id={image.id}
                foreground2={image}
                foreground={image.watermarkUrl}
                actualForeground={foreground?.imageUrl}
                fileName={image.imageFileName}
                favorited={favorites.includes(image.id)}
              />
            );
          })}
        </div>
      </div>

      <div className={styles.btnContainer}>
        <Button
          bold
          circular
          fullWidth
          onClick={favoriteAll}
          variant="primary"
          classes={styles.favoriteAllButton}
        >
          <span className={styles.favoriteAll}>
            {favorites.length === filteredImages.length ? (
              <Image src={fullHeartIcon} alt="Favorite this image" classes={styles.favoriteAllIcon} />
            ) : (
              <Image src={heartIcon} alt="Favorite this image" classes={styles.favoriteAllIcon} />
            )}{' '} Favorite All
          </span>
        </Button>

        <Button
          bold
          circular
          fullWidth
          onClick={onContinue}
          variant="primary"
          classes={favorites.length === 0 ? classes.lowOpacity : ''}
        >
          Continue
        </Button>
      </div>
    </div>
  )
}

export default FavoritePoseSelection
